import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";
import { fmtFull } from "@/lib/dates";
import { ArrowUpRight, BarChart3, Briefcase, Code2, Heart, HeartHandshake, Mic2, Video } from "lucide-react";

export function WorkWithSankalp() {
  const [info, setInfo] = useState(null);
  const [workshops, setWorkshops] = useState([]);
  const { currency, format } = useCurrency();

  useEffect(() => {
    api.get("/catalog/one-on-one").then((r) => setInfo(r.data)).catch(() => {});
    api.get("/catalog/workshops").then((r) => setWorkshops(r.data)).catch(() => {});
  }, []);

  return (
    <section id="work-with-sankalp" className="max-w-7xl mx-auto px-6 pt-4 pb-20" data-testid="work-with-sankalp">
      <div className="text-[11px] uppercase tracking-[0.28em] text-amber-400 font-adult-mono code-underline">Work with Sankalp</div>
      <h2 className="mt-4 font-adult-heading text-3xl md:text-4xl font-semibold tracking-tight">Three ways to learn from Sankalp directly.</h2>
      <div className="mt-10 grid lg:grid-cols-12 gap-5">
        <OneOnOneCard price={info ? format(info[`price_${currency}`]) : ""} />
        <div className="lg:col-span-7 grid gap-5">
          {workshops.map((w) => <WorkshopCard key={w.workshop_id} w={w} price={format(w[`price_${currency}`])} />)}
        </div>
        <InviteCard />
      </div>
    </section>
  );
}

function OneOnOneCard({ price }) {
  const chips = [["Career", Briefcase], ["Tech", Code2], ["Relationships", Heart]];
  return (
    <div className="lg:col-span-5 relative overflow-hidden rounded-3xl border border-amber-500/40 bg-gradient-to-br from-amber-500/15 via-zinc-950 to-zinc-950 p-7 md:p-8 flex flex-col">
      <div className="inline-flex w-fit items-center gap-2 rounded-full bg-amber-500/15 border border-amber-500/40 px-3 py-1 text-[11px] uppercase tracking-[0.2em] font-adult-mono text-amber-300">
        <Video className="w-3 h-3" />1-on-1 · 60 min
      </div>
      <h3 className="mt-6 font-adult-heading text-3xl font-semibold tracking-tight leading-tight">A private hour with Sankalp.</h3>
      <p className="mt-3 text-zinc-300 leading-relaxed">Career moves, tech roadblocks or relationship worries. Talk it through and leave with clear next steps.</p>
      <div className="mt-5 flex flex-wrap gap-2">
        {chips.map(([l, Icon]) => (
          <span key={l} className="inline-flex items-center gap-1.5 text-xs rounded-full border border-zinc-700 px-3 py-1 text-zinc-300"><Icon className="w-3 h-3 text-amber-400" />{l}</span>
        ))}
      </div>
      <div className="mt-auto pt-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-adult-mono">Session fee</div>
          <div data-testid="founder-1on1-price" className="font-adult-mono text-3xl font-semibold text-amber-400 mt-1">{price}</div>
        </div>
        <Link to="/meet-sankalp/1-on-1" data-testid="founder-book-1on1" className="inline-flex items-center gap-2 rounded-full bg-amber-500 hover:bg-amber-400 text-black font-semibold px-6 py-3 text-sm glow-amber transition-smooth">
          Book a 1-on-1 <ArrowUpRight className="w-4 h-4" />
        </Link>
      </div>
    </div>
  );
}

function WorkshopCard({ w, price }) {
  const healing = w.theme === "healing";
  const Icon = healing ? HeartHandshake : BarChart3;
  const tone = healing ? "text-rose-300" : "text-amber-400";
  return (
    <Link to={`/workshops/${w.workshop_id}`} data-testid={`founder-workshop-${w.workshop_id}`}
      className={`group rounded-3xl border p-6 md:p-7 transition-smooth hover:-translate-y-0.5 ${healing ? "border-rose-300/25 bg-gradient-to-br from-rose-400/10 to-[#120C0C] hover:border-rose-300/50" : "border-zinc-800 bg-zinc-950 hover:border-amber-500/50"}`}>
      <div className="flex items-start justify-between gap-4">
        <div className={`w-11 h-11 rounded-xl border flex items-center justify-center ${healing ? "bg-rose-400/10 border-rose-300/30" : "bg-amber-500/10 border-amber-500/30"} ${tone}`}><Icon className="w-5 h-5" /></div>
        <div className={`font-adult-mono text-xl font-semibold ${tone}`}>{price}</div>
      </div>
      <div className="mt-5 text-[10px] uppercase tracking-[0.2em] font-adult-mono text-zinc-500">
        Live workshop · {w.start_time_iso ? fmtFull(w.start_time_iso) : "Date announced soon"}
      </div>
      <h3 className="mt-2 font-adult-heading text-2xl font-semibold tracking-tight">{w.title}</h3>
      <p className="mt-2 text-sm text-zinc-400 leading-relaxed">{w.tagline}</p>
      <div className={`mt-5 inline-flex items-center gap-1.5 text-sm font-medium ${tone}`}>
        View &amp; enrol <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
      </div>
    </Link>
  );
}

function InviteCard() {
  return (
    <div className="lg:col-span-12 rounded-3xl border border-violet-500/30 bg-gradient-to-r from-violet-500/10 via-zinc-950 to-zinc-950 p-7 md:p-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
      <div className="flex items-start gap-5">
        <div className="w-12 h-12 rounded-2xl bg-violet-500/15 border border-violet-500/40 flex items-center justify-center text-violet-300 shrink-0"><Mic2 className="w-5 h-5" /></div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] font-adult-mono text-violet-300">Campus &amp; workplace talks · 90 min</div>
          <h3 className="mt-2 font-adult-heading text-2xl md:text-3xl font-semibold tracking-tight">Invite Sankalp to your college or company.</h3>
          <p className="mt-2 text-sm text-zinc-400 max-w-xl leading-relaxed">A 90-minute live session for your students or team. Tell us about your event and Sankalp&apos;s team will get in touch.</p>
        </div>
      </div>
      <Link to="/invite-sankalp" data-testid="founder-invite-btn" className="inline-flex items-center justify-center gap-2 rounded-full bg-violet-500 hover:bg-violet-400 text-white font-semibold px-6 py-3 text-sm shrink-0 transition-smooth shadow-[0_0_24px_rgba(139,92,246,0.45)]">
        Invite Sankalp Sidhanth <ArrowUpRight className="w-4 h-4" />
      </Link>
    </div>
  );
}
